import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Heading,
  Link,
  Select,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import { Link as RouterLink } from "react-router-dom";
import { ReviewQueueApi, type ReviewQueueRow } from "../api/reviewQueue";
import ReviewQueueModal from "../components/ReviewQueueModal";
import logger from "../analytics/logger";

export default function ReviewQueue() {
  const toast = useToast();
  const [rows, setRows] = useState<ReviewQueueRow[]>([]);
  const [reviewers, setReviewers] = useState<string[]>([]);
  const [me, setMe] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [selected, setSelected] = useState<ReviewQueueRow | null>(null);
  const { isOpen, onOpen, onClose } = useDisclosure();

  const load = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const [list, directory] = await Promise.all([
        ReviewQueueApi.list(),
        ReviewQueueApi.reviewers(),
      ]);
      setRows(list.items);
      setReviewers(directory.reviewers);
      setMe(directory.me);
    } catch (e) {
      logger.error("review_queue_load_failed", e);
      setError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  // The logged-in support user goes first — they assign work to themselves far
  // more often than to anyone else.
  const orderedReviewers = useMemo(() => {
    if (!me) return reviewers;
    return [me, ...reviewers.filter((r) => r !== me)];
  }, [reviewers, me]);

  async function assign(row: ReviewQueueRow, reviewer: string) {
    try {
      await ReviewQueueApi.update(row.work_id, { reviewer: reviewer || null });
      await load();
    } catch (e) {
      logger.error("review_queue_assign_failed", e);
      toast({ status: "error", title: "Could not assign reviewer" });
    }
  }

  function openRow(row: ReviewQueueRow) {
    setSelected(row);
    onOpen();
  }

  if (loading) return <Spinner m={8} />;
  if (error) return <Text m={8}>Could not load the review queue.</Text>;

  return (
    <Box p={6}>
      <Heading size="lg" mb={4}>
        Review queue
      </Heading>

      {rows.length === 0 ? (
        <Text>No works are currently awaiting review.</Text>
      ) : (
        <Table size="sm">
          <Thead>
            <Tr>
              <Th>Type</Th>
              <Th>Title</Th>
              <Th>Creator profile</Th>
              <Th>Reviewer</Th>
            </Tr>
          </Thead>
          <Tbody>
            {rows.map((row) => (
              <Tr
                key={row.work_id}
                onClick={() => openRow(row)}
                cursor="pointer"
                _hover={{ bg: "gray.50" }}
              >
                <Td>{row.type ?? "—"}</Td>
                <Td>
                  {/* stopPropagation on every interactive cell: without it a
                      click on the link ALSO opens the modal behind the
                      navigation. */}
                  <Link
                    as={RouterLink}
                    to={`/works/${row.work_id}`}
                    color="blue.600"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {row.title || "(untitled)"}
                  </Link>
                </Td>
                <Td>
                  {row.creator_id ? (
                    <Link
                      as={RouterLink}
                      to={`/creators/${row.creator_id}`}
                      color="blue.600"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {row.profile_name ?? "—"}
                    </Link>
                  ) : (
                    (row.profile_name ?? "—")
                  )}
                </Td>
                <Td onClick={(e) => e.stopPropagation()}>
                  <Select
                    size="sm"
                    value={row.reviewer ?? ""}
                    placeholder="Unassigned"
                    onChange={(e) => void assign(row, e.target.value)}
                  >
                    {orderedReviewers.map((r) => (
                      <option key={r} value={r}>
                        {r}
                      </option>
                    ))}
                  </Select>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      )}

      <ReviewQueueModal
        row={selected}
        reviewers={orderedReviewers}
        isOpen={isOpen}
        onClose={onClose}
        onSaved={() => void load()}
      />
    </Box>
  );
}
